import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import { useCompany } from "../../contexts/CompanyContext";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import syntheticStockMoveService from "../../services/syntheticStockMoveService";
import SyntheticOutboundStockMoveForm from "../LGCrud/SyntheticOutboundStockMoveForm";
import dayjs from "dayjs";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import { getProviderList } from "../../api/providers";
import { usePersistedPagination } from "../../hooks/usePersistedPagination";

const AccountingOutboundsTable = () => {
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();

  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [paginationModel, setPaginationModel] = usePersistedPagination({
    defaultPageSize: 25,
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [moveDate, setMoveDate] = useState(dayjs().format("YYYY-MM-DDTHH:mm"));
  const [editingOutboundId, setEditingOutboundId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!selectedCompany) return;

    setLoading(true);
    try {
      const result = await syntheticStockMoveService.getSyntheticInbounds(
        selectedCompany.id,
        startDate?.toDate(),
        endDate?.toDate(),
        selectedProvider?.code || null
      );
      setData(result);
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, startDate, endDate, selectedProvider, openSnack]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateOutboundsModal = () => {
    if (selectedRows.length === 0) {
      openSnack("Seleccione al menos una entrada", "warning");
      return;
    }
    setMoveDate(dayjs().format("YYYY-MM-DDTHH:mm"));
    setIsCreateModalOpen(true);
  };

  const closeCreateOutboundsModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleEditOutbound = useCallback((outboundId) => {
    setEditingOutboundId(outboundId);
  }, []);

  const closeEditOutboundModal = useCallback(() => {
    setEditingOutboundId(null);
  }, []);

  const handleEditOutboundSuccess = useCallback(async () => {
    setEditingOutboundId(null);
    await fetchData();
  }, [fetchData]);

  const handleCreateOutbounds = async () => {
    if (!moveDate) {
      openSnack("Seleccione una fecha y hora para la salida", "warning");
      return;
    }

    if (selectedRows.length === 0) {
      openSnack("Seleccione al menos una entrada", "warning");
      return;
    }

    try {
      setLoading(true);
      await syntheticStockMoveService.createSyntheticOutbounds({
        StockMoveIds: selectedRows,
        CompanyId: selectedCompany.id,
        MoveDate: dayjs(moveDate).toDate(),
      });
      openSnack("Salidas contables creadas exitosamente", "success");
      setSelectedRows([]);
      closeCreateOutboundsModal();
      await fetchData();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "poCode",
      headerName: "Código OC",
      flex: 0.12,
    },
    {
      field: "providerName",
      headerName: "Proveedor",
      flex: 0.18,
    },
    {
      field: "productCode",
      headerName: "Producto",
      flex: 0.14,
    },
    {
      field: "quantity",
      headerName: "Cantidad",
      flex: 0.08,
      type: "number",
    },
    {
      field: "unitCost",
      headerName: "Costo unitario",
      flex: 0.1,
      type: "number",
    },
    {
      field: "createdAt",
      headerName: "Fecha de Creación",
      flex: 0.14,
      valueFormatter: (value) => {
        if (!value) return "-";
        return dayjs(value).format("DD/MM/YYYY HH:mm");
      },
    },
    {
      field: "createdBy",
      headerName: "Creado por",
      flex: 0.12,
    },
    {
      field: "hasOutbounds",
      headerName: "Estado",
      flex: 0.12,
      renderCell: (params) => {
        return (
          <Chip
            size="small"
            label={params.row.hasOutbounds ? "Salidas Creadas" : "Pendiente"}
            color={params.row.hasOutbounds ? "success" : "warning"}
          />
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Acciones",
      width: 80,
      getActions: (params) => {
        if (!params.row.hasOutbounds || !params.row.outboundStockMoveId) {
          return [];
        }
        return [
          <GridActionsCellItem
            key={`edit-${params.id}`}
            icon={<EditIcon />}
            label="Editar salida"
            className="textPrimary"
            onClick={() => handleEditOutbound(params.row.outboundStockMoveId)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const selectableRows = data
    .filter((row) => !row.hasOutbounds)
    .map((row) => row.stockMoveId);

  if (!selectedCompany) {
    return (
      <Box p={4}>
        <Typography>Seleccione una compañía primero</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <MyDatePicker
              label="Fecha Inicial"
              value={startDate}
              onChange={(newValue) => setStartDate(dayjs(newValue))}
            />
            <MyDatePicker
              label="Fecha Final"
              value={endDate}
              onChange={(newValue) => setEndDate(dayjs(newValue))}
            />
            <Box sx={{ minWidth: 200 }}>
              <LGAutocomplete
                fetchFunction={getProviderList}
                queryParams={{ companyId: selectedCompany?.id }}
                optionLabel="code"
                value={selectedProvider}
                onChange={(event, value) => {
                  setSelectedProvider(value);
                }}
                label="Proveedor"
              />
            </Box>

            <Chip
              label={`${data.length} entradas sintéticas`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${selectableRows.length} pendientes`}
              color="warning"
              variant="outlined"
            />
          </Stack>

          <Button
            variant="contained"
            color="primary"
            onClick={openCreateOutboundsModal}
            disabled={loading || selectedRows.length === 0}
          >
            Crear Salidas ({selectedRows.length})
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={2} sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={data}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => {
            const validSelection = newSelection.filter((id) =>
              selectableRows.includes(id)
            );
            setSelectedRows(validSelection);
          }}
          rowSelectionModel={selectedRows}
          getRowId={(row) => row.stockMoveId}
          isRowSelectable={(params) => !params.row.hasOutbounds}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Dialog
        open={isCreateModalOpen}
        onClose={closeCreateOutboundsModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Crear salidas contables</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Fecha y hora de movimiento"
            type="datetime-local"
            fullWidth
            value={moveDate}
            onChange={(event) => setMoveDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateOutboundsModal} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateOutbounds}
            disabled={loading || !moveDate}
          >
            Crear salidas
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editingOutboundId != null}
        onClose={closeEditOutboundModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar salida contable</DialogTitle>
        <DialogContent>
          {editingOutboundId != null && (
            <SyntheticOutboundStockMoveForm
              id={editingOutboundId}
              companyId={selectedCompany?.id}
              onSubmit={handleEditOutboundSuccess}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AccountingOutboundsTable;
